// SALES TAX ON SCREEN (D3.16; docs/adr-sales-tax.md §6, docs/ux/2026-10-24-club-sales-tax.md).
//
// The word is always "tax" — never a country's name. Every string a screen shows about tax is
// built here, so the copy lives in one place and an untaxed club renders exactly what it did.
//
// TWO KINDS OF FIGURE, NEVER MIXED:
//   - PAYABLE figures (a pay button, a bill, a receipt, a ledger) are the SERVER's own
//     `{ netMinor, taxMinor, totalMinor, taxRateBps }` — `readTaxFields` reads them, nothing here
//     computes what is charged.
//   - PUBLIC PRICE LISTS carry the pre-tax price plus the club's `salesTax.rateBps`. For those
//     only, `taxLine` mirrors `backend/src/modules/shared/salesTax.mjs#taxLine`, held to it by a
//     parity test over `backend/tests/fixtures/sales-tax-cases.json` (taxDisplay.test.mjs).

import { formatMoney } from './formatMoney'

const BPS = 10000
const MIN_BPS = 1
const MAX_BPS = 5000

export type TaxLine = { netMinor: number; taxMinor: number; totalMinor: number; rateBps: number }

/** The server's tax read fields, present only on a stamped record (ADR §6). */
export type TaxReadFields = {
  netMinor?: number
  taxMinor?: number
  totalMinor?: number
  taxRateBps?: number | null
}

/** A club read's `salesTax` (present only while tax is on). The manager's read may add `enabled`. */
export type ClubSalesTax = { rateBps?: number | null; enabled?: boolean } | null | undefined

const isRate = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= MIN_BPS && value <= MAX_BPS

/** THE RULE (mirror): tax = floor(net × rateBps / 10000), integers only; total = net + tax. */
export function taxLine(netMinor: number, rateBps: number): TaxLine {
  if (!Number.isSafeInteger(netMinor) || netMinor < 0) throw new TypeError(`taxLine: netMinor ${netMinor}`)
  if (!(rateBps === 0 || isRate(rateBps))) throw new TypeError(`taxLine: rateBps ${rateBps}`)
  const taxMinor = Math.floor((netMinor * rateBps) / BPS)
  return { netMinor, taxMinor, totalMinor: netMinor + taxMinor, rateBps }
}

/** The club's rate while tax is on, else null. A club read with `enabled: false` is off. */
export function clubTaxRate(salesTax: ClubSalesTax): number | null {
  if (!salesTax || salesTax.enabled === false) return null
  return isRate(salesTax.rateBps) ? salesTax.rateBps : null
}

/** `{rate}`: 1800 → "18", 1650 → "16.5", 1 → "0.01". */
export function formatTaxRate(rateBps: number): string {
  return String(Number((rateBps / 100).toFixed(2)))
}

/** `Tax ({rate}%)`, or `Tax` when no single rate applies (/ux §13 ruling 1). */
export function taxLabel(rateBps: number | null | undefined): string {
  return isRate(rateBps) ? `Tax (${formatTaxRate(rateBps)}%)` : 'Tax'
}

export const pricesIncludeTax = (rateBps: number) => `Prices include tax (${formatTaxRate(rateBps)}%).`
export const includesTax = (rateBps: number) => `Includes tax (${formatTaxRate(rateBps)}%).`
export const feesIncludeTax = (rateBps: number) => `Fees include tax (${formatTaxRate(rateBps)}%).`

/** A public list price (major units, pre-tax) as the player pays it. Untaxed → unchanged. */
export function priceWithTax(amount: number, rateBps: number | null): number {
  if (rateBps === null || !Number.isFinite(amount) || amount < 0) return amount
  return taxLine(Math.round(amount * 100), rateBps).totalMinor / 100
}

/** A public list amount in MINOR units as the player pays it. Untaxed → unchanged. */
export function minorWithTax(minor: number, rateBps: number | null): number {
  return rateBps === null || !Number.isSafeInteger(minor) || minor < 0 ? minor : taxLine(minor, rateBps).totalMinor
}

/** Organizer price input hint: `Players pay {total} with tax ({rate}%).` Null when untaxed or empty. */
export function playersPayHint(amount: number | null, currency: string | null | undefined, rateBps: number | null): string | null {
  if (rateBps === null || amount === null || !Number.isFinite(amount) || amount <= 0) return null
  return `Players pay ${formatMoney(priceWithTax(amount, rateBps), currency)} with tax (${formatTaxRate(rateBps)}%).`
}

/** The server's split on a stamped record, or null (untaxed — show one amount, as today). */
export function readTaxFields(record: TaxReadFields | null | undefined): TaxLine | null {
  if (!record) return null
  const { netMinor, taxMinor, totalMinor, taxRateBps } = record
  if (![netMinor, taxMinor, totalMinor].every((n) => typeof n === 'number' && Number.isSafeInteger(n))) return null
  if (!isRate(taxRateBps)) return null
  return { netMinor: netMinor!, taxMinor: taxMinor!, totalMinor: totalMinor!, rateBps: taxRateBps }
}

/** What a record charges, in major units: the server's TOTAL when stamped, else its own amount. */
export function chargeOf(record: TaxReadFields | null | undefined, amount: number): number {
  const line = readTaxFields(record)
  return line ? line.totalMinor / 100 : amount
}

/**
 * A SHARE as the browser models it: `payableMinor` is what it charges — the server's TOTAL when
 * the seat is stamped (ADR §6 puts `{ netMinor, taxMinor, totalMinor, taxRateBps }` beside it).
 * Applied once, where a roster is read, so every pay prompt, pay-all sum and balance check
 * downstream speaks the amount the wallet or the card actually takes.
 */
export function withTaxedPayable<T extends TaxReadFields & { payableMinor: number }>(seat: T): T {
  const line = readTaxFields(seat)
  return line ? { ...seat, payableMinor: line.totalMinor } : seat
}

/**
 * THE TAX TWINS, FOLDED (ADR §7.4, §7.7): a money fact that names its tax beside it —
 * `owedMinor` + `owedTaxMinor` — read as the one total the player pays or gets back. Each pair is
 * `[amount, twin]`; a missing twin (an untaxed record) adds 0, so an untaxed object comes back as it was.
 */
export function foldTwins<T extends object>(source: T, pairs: ReadonlyArray<readonly [keyof T & string, string]>): T {
  const raw = source as Record<string, unknown>
  const out: Record<string, unknown> = { ...raw }
  let changed = false
  for (const [key, twin] of pairs) {
    const tax = raw[twin]
    if (typeof raw[key] !== 'number' || typeof tax !== 'number' || !Number.isSafeInteger(tax) || tax <= 0) continue
    out[key] = (raw[key] as number) + tax
    changed = true
  }
  return changed ? (out as T) : source
}

/** A MAJOR amount with its tax twin (MINOR units) folded in — the same amount when the twin is absent or 0. */
export function plusTaxMinor<T extends number | null | undefined>(amount: T, taxMinor: unknown): T {
  if (typeof amount !== 'number' || typeof taxMinor !== 'number' || !Number.isSafeInteger(taxMinor) || taxMinor <= 0) return amount
  return (Math.round(amount * 100 + taxMinor) / 100) as T
}

/** Only the four tax read fields off a wire row — `{}` untaxed — to carry onto a browser model. */
export function taxFieldsOf(raw: TaxReadFields | null | undefined): TaxReadFields {
  const line = readTaxFields(raw)
  return line ? { netMinor: line.netMinor, taxMinor: line.taxMinor, totalMinor: line.totalMinor, taxRateBps: line.rateBps } : {}
}

/** Several server lines (a split booking's shares) added up — never recomputed (ADR §3.7). */
export function sumTaxLines(lines: Array<TaxLine | null>): TaxLine | null {
  const taxed = lines.filter((line): line is TaxLine => line !== null)
  if (taxed.length === 0) return null
  const sum = (key: 'netMinor' | 'taxMinor' | 'totalMinor') => taxed.reduce((acc, line) => acc + line[key], 0)
  const rates = new Set(taxed.map((line) => line.rateBps))
  return { netMinor: sum('netMinor'), taxMinor: sum('taxMinor'), totalMinor: sum('totalMinor'), rateBps: rates.size === 1 ? taxed[0].rateBps : 0 }
}

/** The small line under a taxed report amount: `incl. {tax} tax`. Null when there is none. */
export function inclTaxNote(taxAmount: number | null | undefined, currency: string | null | undefined): string | null {
  return typeof taxAmount === 'number' && taxAmount > 0 ? `incl. ${formatMoney(taxAmount, currency)} tax` : null
}

/** A refund outcome's stored `taxMinor` as the sentence's ` (incl. {tax} tax)`, or '' (/ux §7). */
export function refundTaxNote(taxMinor: unknown, currency: string | null | undefined): string {
  return typeof taxMinor === 'number' && Number.isSafeInteger(taxMinor) && taxMinor > 0 ? refundTaxSuffix(taxMinor / 100, currency) : ''
}

/** Appended to a refund / credit sentence: ` (incl. {tax} tax)`, or '' when untaxed. */
export function refundTaxSuffix(taxAmount: number | null | undefined, currency: string | null | undefined): string {
  return typeof taxAmount === 'number' && taxAmount > 0 ? ` (incl. ${formatMoney(taxAmount, currency)} tax)` : ''
}
